/**
 * Sprint 10T: Order Tracking V2
 * 
 * World-class tracking UI with:
 * - Live map showing vendor + rider locations
 * - Timeline with animations
 * - Delay insights and ETA
 * - SLA status indicators
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import { 
  ArrowLeft, 
  MapPin, 
  Bike, 
  Clock, 
  CheckCircle, 
  AlertTriangle,
  ChefHat,
  Truck,
  Home,
  Phone
} from 'lucide-react';
import { formatOrderId, formatTime } from '@/lib/format';
import { api } from '@/lib/api';
import { logger } from '@/lib/logger';

// ============================================================================
// TYPES
// ============================================================================

interface TrackingV2Data {
  orderId: string;
  status: string;
  createdAt: string;
  vendor: {
    id: string;
    name: string;
    lat: number | null;
    lng: number | null;
    phone?: string;
  };
  rider: {
    id: string;
    name: string;
    lat: number | null;
    lng: number | null;
    lastUpdateAt: string | null;
    phone?: string;
    isOnline: boolean;
  } | null;
  buyer: {
    lat: number | null;
    lng: number | null;
    address: string;
  };
  eta: {
    prepEtaMinutes: number;
    travelEtaMinutes: number;
    totalEtaMinutes: number;
    promisedDeliveryAt: string | null;
  };
  sla: {
    status: string;
    statusLabel: string;
    delayMinutes: number;
    attribution: string;
  };
  timeline: {
    orderCreatedAt: string;
    orderAcceptedAt: string | null;
    preparingAt: string | null;
    riderAssignedAt: string | null;
    pickedUpAt: string | null;
    deliveringAt: string | null;
    deliveredAt: string | null;
  };
  delayInsight: {
    isLate: boolean;
    reasonCode: string;
    message: string;
    severity: string;
  };
  computed: {
    remainingMinutes: number | null;
    currentStage: number;
    stages: Array<{ key: string; label: string; step: number }>;
  };
}

// ============================================================================
// CUSTOM MAP ICONS
// ============================================================================

const createIcon = (emoji: string, color: string) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background: ${color};
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        border: 3px solid white;
      ">${emoji}</div>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 40],
  });
};

const vendorIcon = createIcon('🏪', '#9333ea');
const riderIcon = createIcon('🛵', '#2563eb');
const buyerIcon = createIcon('🏠', '#16a34a');

// ============================================================================
// TIMELINE STAGES
// ============================================================================

const TRACKING_STAGES = [
  { key: 'accepted', label: 'Order Accepted', icon: CheckCircle, step: 1 },
  { key: 'preparing', label: 'Preparing', icon: ChefHat, step: 2 },
  { key: 'rider_assigned', label: 'Rider Assigned', icon: Bike, step: 3 },
  { key: 'picked_up', label: 'Rider On the Way', icon: Truck, step: 4 },
  { key: 'arriving', label: 'Arriving Soon', icon: MapPin, step: 5 },
  { key: 'delivered', label: 'Delivered', icon: Home, step: 6 },
];

// ============================================================================
// COMPONENT
// ============================================================================

export function TrackingV2() {
  const { id: orderId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [tracking, setTracking] = useState<TrackingV2Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pollInterval, setPollInterval] = useState<NodeJS.Timeout | null>(null);

  // Fetch tracking data
  const fetchTracking = useCallback(async () => {
    if (!orderId) return;
    
    try {
      const response = await api.get(`/api/tracking/v2/orders/${orderId}`);
      if (response.data.success) {
        setTracking(response.data.data);
        setError(null);
      } else {
        setError(response.data.error?.message || 'Failed to load tracking');
      }
    } catch (err: any) {
      logger.error('Failed to fetch tracking v2', err);
      // Try fallback to v1
      if (err.response?.status === 503) {
        setError('Tracking v2 is not available. Using classic view.');
        navigate(`/order/${orderId}`, { replace: true });
      } else {
        setError('Failed to load tracking information');
      }
    } finally {
      setLoading(false);
    }
  }, [orderId, navigate]);

  // Initial fetch and polling
  useEffect(() => {
    fetchTracking();
    
    // Poll every 10 seconds for active orders
    const interval = setInterval(fetchTracking, 10000);
    setPollInterval(interval);
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [fetchTracking]);

  // Stop polling when order is delivered
  useEffect(() => {
    if (tracking?.status === 'delivered' || tracking?.status === 'cancelled') {
      if (pollInterval) {
        clearInterval(pollInterval);
        setPollInterval(null);
      }
    }
  }, [tracking?.status, pollInterval]);

  // Get SLA status color
  const getSlaColor = (status: string) => {
    switch (status) {
      case 'on_time': return 'bg-green-500';
      case 'late_0_10': return 'bg-yellow-500';
      case 'late_10_20': return 'bg-orange-500';
      case 'late_20_plus': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  // Get delay severity color
  const getDelaySeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'bg-red-50 border-red-200 text-red-800';
      case 'medium': return 'bg-amber-50 border-amber-200 text-amber-800';
      case 'low': return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      default: return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading tracking...</p>
        </div>
      </div>
    );
  }

  if (error || !tracking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Tracking Unavailable</h2>
          <p className="text-gray-600 mb-4">{error || 'Order not found'}</p>
          <button 
            onClick={() => navigate('/orders')}
            className="bg-green-600 text-white px-6 py-2 rounded-lg"
          >
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  const { vendor, rider, buyer, eta, sla, timeline, delayInsight, computed } = tracking;
  const mapCenter: [number, number] = [
    buyer.lat || vendor.lat || 13.15,
    buyer.lng || vendor.lng || 123.73,
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/orders')}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1">
              <h1 className="font-bold">{formatOrderId(tracking.orderId)}</h1>
              <p className="text-sm text-gray-500">{vendor.name}</p>
            </div>
            <div className={`px-3 py-1 rounded-full text-xs font-medium text-white ${getSlaColor(sla.status)}`}>
              {sla.statusLabel}
            </div>
          </div>
        </div>
      </header>

      {/* Map Section */}
      <div className="relative h-[40vh] min-h-[280px]">
        <MapContainer
          center={mapCenter}
          zoom={14}
          className="h-full w-full z-0"
          zoomControl={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {/* Vendor Marker */}
          {vendor.lat && vendor.lng && (
            <Marker position={[vendor.lat, vendor.lng]} icon={vendorIcon}>
              <Popup>
                <div className="text-center">
                  <strong>{vendor.name}</strong>
                  <br />
                  <span className="text-gray-500">Vendor</span>
                </div>
              </Popup>
            </Marker>
          )}
          
          {/* Rider Marker */}
          {rider?.lat && rider?.lng && (
            <Marker position={[rider.lat, rider.lng]} icon={riderIcon}>
              <Popup>
                <div className="text-center">
                  <strong>{rider.name}</strong>
                  <br />
                  <span className="text-gray-500">Rider</span>
                  {rider.lastUpdateAt && (
                    <div className="text-xs text-gray-400 mt-1">
                      Updated: {formatTime(rider.lastUpdateAt)}
                    </div>
                  )}
                </div>
              </Popup>
            </Marker>
          )}
          
          {/* Buyer Marker */}
          {buyer.lat && buyer.lng && (
            <Marker position={[buyer.lat, buyer.lng]} icon={buyerIcon}>
              <Popup>
                <div className="text-center">
                  <strong>Delivery Location</strong>
                  <br />
                  <span className="text-xs text-gray-500">{buyer.address}</span>
                </div>
              </Popup>
            </Marker>
          )}

          {/* Route Line */}
          {rider?.lat && rider?.lng && buyer.lat && buyer.lng && (
            <Polyline
              positions={[[rider.lat, rider.lng], [buyer.lat, buyer.lng]]}
              color="#2563eb"
              weight={4}
              dashArray="10, 10"
            />
          )}
        </MapContainer>

        {/* ETA Overlay */}
        <div className="absolute bottom-4 left-4 right-4 z-10">
          <div className="bg-white rounded-xl shadow-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Clock className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600">
                    {computed.remainingMinutes !== null 
                      ? `${computed.remainingMinutes} min`
                      : 'Calculating...'
                    }
                  </div>
                  <div className="text-sm text-gray-500">Estimated arrival</div>
                </div>
              </div>
              
              {/* Contact buttons */}
              <div className="flex gap-2">
                {rider?.phone && (
                  <a 
                    href={`tel:${rider.phone}`}
                    className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center"
                  >
                    <Phone className="w-5 h-5 text-blue-600" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">
        {/* Delay Insight */}
        {delayInsight.isLate && (
          <div className={`border rounded-xl p-4 ${getDelaySeverityColor(delayInsight.severity)}`}>
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-medium">Running a bit late</div>
                <div className="text-sm opacity-80">{delayInsight.message}</div>
                <div className="text-xs opacity-60 mt-1">We're monitoring this delivery</div>
              </div>
            </div>
          </div>
        )}

        {/* Timeline */}
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <h3 className="font-bold mb-4">Order Progress</h3>
          
          <div className="relative">
            {/* Progress line */}
            <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200" />
            <div 
              className="absolute left-5 top-0 w-0.5 bg-green-500 transition-all duration-500"
              style={{ height: `${Math.min(100, (computed.currentStage / 6) * 100)}%` }}
            />
            
            <div className="space-y-4">
              {TRACKING_STAGES.map((stage) => {
                const isPast = computed.currentStage > stage.step;
                const isCurrent = computed.currentStage === stage.step;
                const Icon = stage.icon;
                
                // Get timestamp for this stage
                const timestamps: Record<string, string | null> = {
                  accepted: timeline.orderAcceptedAt,
                  preparing: timeline.preparingAt,
                  rider_assigned: timeline.riderAssignedAt,
                  picked_up: timeline.pickedUpAt,
                  arriving: timeline.deliveringAt,
                  delivered: timeline.deliveredAt,
                };
                const timestamp = timestamps[stage.key];
                
                return (
                  <div 
                    key={stage.key}
                    className={`relative flex items-center gap-4 pl-2 transition-all duration-300 ${
                      isCurrent ? 'scale-105' : ''
                    }`}
                  >
                    {/* Circle */}
                    <div 
                      className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                        isPast 
                          ? 'bg-green-500 text-white' 
                          : isCurrent 
                            ? stage.key === 'delivered'
                              ? 'bg-red-500 text-white ring-4 ring-red-200 animate-pulse'
                              : 'bg-green-500 text-white ring-4 ring-green-200 animate-pulse'
                            : 'bg-gray-200 text-gray-400'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1">
                      <div className={`font-medium ${
                        isCurrent 
                          ? stage.key === 'delivered' ? 'text-red-600' : 'text-green-600' 
                          : isPast ? 'text-gray-700' : 'text-gray-400'
                      }`}>
                        {stage.label}
                      </div>
                      {timestamp && (
                        <div className="text-xs text-gray-500">
                          {formatTime(timestamp)}
                        </div>
                      )}
                    </div>
                    
                    {/* Status indicator */}
                    {isCurrent && stage.key !== 'delivered' && (
                      <div className="text-xs text-green-600 font-medium bg-green-50 px-2 py-1 rounded-full">
                        In Progress
                      </div>
                    )}
                    {isCurrent && stage.key === 'delivered' && (
                      <div className="text-xs text-red-600 font-medium bg-red-50 px-2 py-1 rounded-full animate-pulse">
                        Complete!
                      </div>
                    )}
                    {isPast && (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ETA Breakdown */}
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <h3 className="font-bold mb-3">Delivery Estimate</h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="bg-purple-50 rounded-lg p-3">
              <ChefHat className="w-6 h-6 text-purple-600 mx-auto mb-1" />
              <div className="text-lg font-bold text-purple-600">{eta.prepEtaMinutes}m</div>
              <div className="text-xs text-gray-500">Prep Time</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-3">
              <Bike className="w-6 h-6 text-blue-600 mx-auto mb-1" />
              <div className="text-lg font-bold text-blue-600">{eta.travelEtaMinutes}m</div>
              <div className="text-xs text-gray-500">Travel Time</div>
            </div>
            <div className="bg-green-50 rounded-lg p-3">
              <Clock className="w-6 h-6 text-green-600 mx-auto mb-1" />
              <div className="text-lg font-bold text-green-600">{eta.totalEtaMinutes}m</div>
              <div className="text-xs text-gray-500">Total</div>
            </div>
          </div>
          
          {eta.promisedDeliveryAt && (
            <div className="mt-3 text-center text-sm text-gray-500">
              Expected by: <strong>{formatTime(eta.promisedDeliveryAt)}</strong>
            </div>
          )}
        </div>

        {/* Delivery Address */}
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <MapPin className="w-5 h-5 text-green-600" />
            </div>
            <div className="flex-1">
              <div className="text-sm text-gray-500">Delivering to</div>
              <div className="font-medium">{buyer.address || 'Loading address...'}</div>
            </div>
          </div>
        </div>

        {/* Rider Info */}
        {rider && (
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-2xl">
                🛵
              </div>
              <div className="flex-1">
                <div className="font-medium">{rider.name}</div>
                <div className="text-sm text-gray-500">Your Rider</div>
                {!rider.isOnline && (
                  <div className="text-xs text-amber-600 mt-0.5">Location updating...</div>
                )}
              </div>
              {rider.phone && (
                <a 
                  href={`tel:${rider.phone}`}
                  className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center"
                >
                  <Phone className="w-5 h-5 text-green-600" />
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TrackingV2;



